import F84Game from "../../Game";

export class LoaderPlugin extends Phaser.Plugins.BasePlugin {
	constructor(pluginManager: Phaser.Plugins.PluginManager) {
		super(pluginManager);

		pluginManager.registerFileType("webfont", this.webFontFileCallback);
		pluginManager.registerFileType("lottie", this.lottieFileCallback);
	}

	private webFontFileCallback(key: string, url: string): Phaser.Loader.LoaderPlugin {
		const loader: Phaser.Loader.LoaderPlugin = <Phaser.Loader.LoaderPlugin>(<unknown>this);
		const file = new WebFontFile(loader, {
			type: "webfont",
			key: key,
			url: url,
		});
		loader.addFile(file);
		return loader;
	}

	private lottieFileCallback(
		key: string,
		url: string,
		width: number,
		height: number,
		spritesheet?: string,
		spritesheetData?: SpriteAtlas,
		backgroundOnly?: boolean
	): Phaser.Loader.LoaderPlugin {
		const loader: Phaser.Loader.LoaderPlugin = <Phaser.Loader.LoaderPlugin>(<unknown>this);
		const file = new LottieFile(
			loader,
			{
				type: "lottie",
				key: key,
				url: url,
			},
			width,
			height,
			spritesheet,
			spritesheetData,
			backgroundOnly
		);
		loader.addFile(file);
		return loader;
	}
}

export class WebFontFile extends Phaser.Loader.File {
	load(): void {
		const nextFile = (): void => this.loader.nextFile(this, true);
		const font = new FontFaceObserver(<string>this.url);
		font.load(null, 10000).then(
			function () {
				nextFile();
			},
			function () {
				nextFile();
			}
		);
	}

	onProcess(): void {
		this.onProcessComplete();
	}
}

export class LottieFile extends Phaser.Loader.File {
	private _width: number;
	private _height: number;
	private _spritesheet: string;
	private _spritesheetData: SpriteAtlas;
	private _backgroundOnly: boolean;
	constructor(
		loader: Phaser.Loader.LoaderPlugin,
		fileConfig: Phaser.Types.Loader.FileConfig,
		width: number,
		height: number,
		spritesheet?: string,
		spritesheetData?: SpriteAtlas,
		backgroundOnly?: boolean
	) {
		super(loader, fileConfig);
		this._width = width;
		this._height = height;
		this._spritesheet = spritesheet;
		this._spritesheetData = spritesheetData;
		this._backgroundOnly = backgroundOnly;
	}

	load(): void {
		if (this.loader.textureManager.exists(this.key)) {
			this.loader.nextFile(this, true);
			return;
		}

		//hack, shouldnt need to reference resolution in game code
		this._width *= F84Game.Instance.quality.resolution;
		this._height *= F84Game.Instance.quality.resolution;

		const div = (<any>window).document.createElement("div");
		div.style.width = this._width + "px";
		div.style.height = this._height + "px";

		(<any>window).lottie.setQuality("low"); //todo: change this based on device specs

		//Note: By setting autoplay to true and then stopping the animation on data_ready, we are able to fix any preload issues we may have when rendering these as a sprite for the first time
		<any>window.document.body.appendChild(div);
		const animation = (<any>window).lottie.loadAnimation({
			renderer: "canvas",
			context: this.loader.scene.game.canvas.getContext("2d"),
			container: div, // window.document.getElementById("content"),
			loop: false,
			autoplay: true,
			path: this.url,
			rendererSettings: {
				preserveAspectRatio: "xMidYMid meet",
				dpr: 1,
			},

			spritesheet: this._spritesheet,
			spritesheetData: this._spritesheetData,
		});
		const onDataReady = ((): void => {
			animation.stop();
			animation.removeEventListener("data_ready", onDataReady);
		}).bind(this);
		animation.addEventListener("data_ready", onDataReady);

		const onDomReady = ((): void => {
			animation.key = this.key;
			animation.name = this.key;
			animation.baseWidth = this._width;
			animation.baseHeight = this._height;
			if (this._backgroundOnly != true) {
				this.loader.textureManager.addCanvas(this.key, animation.renderer.canvasContext.canvas);
			}
			this.loader.nextFile(this, true);
			window.document.body.removeChild(div);
			animation.removeEventListener("DOMLoaded", onDomReady);
		}).bind(this);
		animation.addEventListener("DOMLoaded", onDomReady);
	}

	onProcess(): void {
		this.onProcessComplete();
	}
}

//typings only
export interface Loader extends Phaser.Loader.LoaderPlugin {
	webfont(key: string, url: string, overwrite?: boolean): Phaser.Loader.LoaderPlugin;
	lottie(
		key: string,
		url: string,
		width: number,
		height: number,
		spritesheet?: string,
		spritesheetData?: SpriteAtlas,
		overwrite?: boolean
	): Phaser.Loader.LoaderPlugin;
	gltf(key: string, url: string): Phaser.Loader.LoaderPlugin;
}
